import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, SlidersHorizontal } from 'lucide-react';

import { ComicCard } from '@/components/ComicCard';
import { PaginatedGrid } from '@/components/PaginatedGrid';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAccountAuthReady } from '@/features/account';
import { useSourceAuth, useSourceAuthLoader } from '@/features/auth';
import { BlockedComicCard, useBlacklist } from '@/features/blacklist';
import { SourceErrorBanner } from '@/features/reader';
import {
  aggregateDisplaySources,
  aggregateSourceSlot,
  runAggregateSourceSearch,
} from '@/features/search';
import { getAllSources } from '@/sources/registry';
import type {
  ComicCard as ComicCardData,
  ComicSource,
  Page,
  RecommendationControlDef,
  RecommendationOption,
  SourceId,
} from '@/sources/types';
import { cn } from '@/lib/utils';

type RecommendationScope = SourceId | 'all';

const SCOPE_PARAM = 'scope';
const OPTION_PARAM_PREFIX = 'option.';
const EXTRA_PARAM_PREFIX = 'extra.';

interface RecommendationResult {
  source: ComicSource;
  option?: RecommendationOption;
  items: ComicCardData[];
  error?: unknown;
  loading: boolean;
}

export function RecommendedPage() {
  const sources = useMemo(() => getAllSources(), []);
  const displayedSources = useMemo(() => aggregateDisplaySources(sources), [sources]);
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { scope, selectedOptions, optionExtras } = useMemo(
    () => recommendationStateFromParams(params, sources),
    [params, sources],
  );
  const activeSource = scope === 'all' ? undefined : sources.find((source) => source.id === scope);
  const [aggregateResults, setAggregateResults] = useState<RecommendationResult[]>([]);
  const [aggregateLoading, setAggregateLoading] = useState(false);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const accountAuthReady = useAccountAuthReady();
  const loadSourceAuth = useSourceAuthLoader();
  const { auth, ready: authReady } = useSourceAuth(activeSource);
  const { isBlocked } = useBlacklist();

  const activeOptions = activeSource?.recommendationOptions ?? [];
  const selectedOptionId =
    activeSource !== undefined ? optionIdForSource(activeSource, selectedOptions) : undefined;
  const aggregateKey = `recommended:${refreshVersion}`;
  const recommendationStateKey = params.toString();

  useEffect(() => {
    if (!accountAuthReady) {
      setAggregateResults([]);
      setAggregateLoading(true);
      return;
    }
    if (scope !== 'all') {
      setAggregateResults([]);
      setAggregateLoading(false);
      return;
    }
    if (sources.length === 0) {
      setAggregateResults([]);
      setAggregateLoading(false);
      return;
    }

    const controller = new AbortController();
    setAggregateLoading(true);
    setAggregateResults(
      displayedSources.map((source) => ({
        source,
        option: optionForSource(source, selectedOptions),
        items: [],
        loading: true,
      })),
    );

    void runAggregateSourceSearch({
      sources,
      query: {},
      loadAuth: loadSourceAuth,
      signal: controller.signal,
      fetchPage: (source, _query, sourceAuth) =>
        source.capabilities.recommendations && source.getRecommendations
          ? source.getRecommendations(
              {
                page: 1,
                optionId: optionForSource(source, selectedOptions)?.id,
                extra: extraForSource(source, optionExtras),
              },
              sourceAuth,
            )
          : source.getPopular({ page: 1 }, sourceAuth),
      onResult: (next) => {
        const result: RecommendationResult = {
          ...next,
          option: optionForSource(next.source, selectedOptions),
        };
        setAggregateResults((current) =>
          current.map((currentResult) =>
            aggregateSourceSlot(currentResult.source) === aggregateSourceSlot(next.source)
              ? result
              : currentResult,
          ),
        );
      },
    }).finally(() => {
      if (!controller.signal.aborted) setAggregateLoading(false);
    });

    return () => {
      controller.abort();
    };
  }, [
    accountAuthReady,
    aggregateKey,
    displayedSources,
    loadSourceAuth,
    optionExtras,
    scope,
    selectedOptions,
    sources,
  ]);

  const scopedFetcher = useCallback(
    async (page: number, cursor?: string): Promise<Page<ComicCardData>> => {
      if (!activeSource) {
        throw new Error('Pick a source before browsing.');
      }
      if (!activeSource.capabilities.recommendations || !activeSource.getRecommendations) {
        return activeSource.getPopular({ page, cursor }, auth);
      }
      return activeSource.getRecommendations(
        {
          page,
          cursor,
          optionId: selectedOptionId,
          extra: extraForSource(activeSource, optionExtras),
        },
        auth,
      );
    },
    [activeSource, auth, optionExtras, selectedOptionId],
  );

  function renderItem(c: ComicCardData) {
    if (isBlocked(c)) return <BlockedComicCard comic={c} />;
    return (
      <ComicCard
        comic={c}
        backState={{
          fromPath: `${location.pathname}${location.search}`,
          fromLabel: 'Discover',
        }}
      />
    );
  }

  function selectSource(nextScope: RecommendationScope) {
    updateRecommendationParams(params, navigate, '/', (next) => {
      if (nextScope === 'all') next.delete(SCOPE_PARAM);
      else next.set(SCOPE_PARAM, nextScope);
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function selectOption(optionId: string) {
    if (!activeSource) return;
    updateRecommendationParams(params, navigate, '/', (next) => {
      next.set(optionParam(activeSource.id), optionId);
    });
  }

  function selectAggregateOption(sourceId: SourceId, optionId: string) {
    updateRecommendationParams(params, navigate, '/', (next) => {
      next.set(optionParam(sourceId), optionId);
    });
  }

  function setControlValue(sourceId: SourceId, fieldId: string, value: string) {
    updateRecommendationParams(params, navigate, '/', (next) => {
      next.set(extraParam(sourceId, fieldId), value);
    });
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3 rounded-xl border border-border/60 bg-card/30 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Recommended</h2>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setRefreshVersion((version) => version + 1)}
            disabled={scope === 'all' && aggregateLoading}
            className="gap-1.5"
            aria-label="Refresh feed"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', aggregateLoading && 'animate-spin')} />
            Refresh
          </Button>
        </div>

        {activeSource && activeOptions.length > 0 && (
          <div
            className="flex max-w-full items-center gap-2 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:thin]"
            role="group"
            aria-label={`${activeSource.name} recommendation options`}
          >
            {activeOptions.map((option) => (
              <RecommendationOptionChip
                key={option.id}
                option={option}
                active={selectedOptionId === option.id}
                onClick={() => selectOption(option.id)}
              />
            ))}
          </div>
        )}
        {activeSource && activeOptions.length > 0 && (
          <RecommendationControls
            source={activeSource}
            option={optionForSource(activeSource, selectedOptions)}
            extras={extraForSource(activeSource, optionExtras)}
            onChange={setControlValue}
          />
        )}
      </section>

      {sources.length === 0 ? (
        <UnsupportedState />
      ) : scope === 'all' ? (
        <AggregateRecommendations
          results={aggregateResults}
          loading={aggregateLoading}
          renderItem={renderItem}
          onOpenSource={selectSource}
          onSelectOption={selectAggregateOption}
          optionExtras={optionExtras}
          onChangeControl={setControlValue}
        />
      ) : activeSource ? (
        authReady ? (
          <PaginatedGrid<ComicCardData>
            key={`${activeSource.id}:${auth ? 'auth' : 'anon'}:${recommendationStateKey}:${refreshVersion}`}
            fetcher={scopedFetcher}
            resetKey={`${activeSource.id}:${auth ? 'auth' : 'anon'}:${recommendationStateKey}:${refreshVersion}`}
            cacheKey={`recommended:${recommendationStateKey}:${auth ? 'auth' : 'anon'}:${refreshVersion}`}
            ariaLabel={`${activeSource.name} ${sourceFeedLabel(activeSource)}`}
            itemKey={(c) => `${c.source}-${c.id}`}
            renderItem={renderItem}
            emptyState={<EmptyRecommendationState source={activeSource} />}
            errorBanner={(err, retry) => (
              <SourceErrorBanner error={err} sourceId={activeSource.id} onRetry={retry} />
            )}
            autoLoadOnScroll
          />
        ) : (
          <RecommendationSkeleton
            ariaLabel={`${activeSource.name} ${sourceFeedLabel(activeSource)} loading`}
          />
        )
      ) : (
        <UnsupportedState />
      )}
    </div>
  );
}

interface RecommendationUrlState {
  scope: RecommendationScope;
  selectedOptions: Record<string, string>;
  optionExtras: Record<string, Record<string, unknown>>;
}

function recommendationStateFromParams(
  params: URLSearchParams,
  sources: readonly ComicSource[],
): RecommendationUrlState {
  const rawScope = params.get(SCOPE_PARAM);
  const scope: RecommendationScope =
    rawScope && sources.some((source) => source.id === rawScope) ? (rawScope as SourceId) : 'all';
  const selectedOptions: Record<string, string> = {};
  const optionExtras: Record<string, Record<string, unknown>> = {};

  for (const source of sources) {
    const optionId = params.get(optionParam(source.id));
    if (optionId && source.recommendationOptions?.some((option) => option.id === optionId)) {
      selectedOptions[source.id] = optionId;
    }

    const controlIds = new Set(
      source.recommendationOptions?.flatMap(
        (option) => option.controls?.map((field) => field.id) ?? [],
      ) ?? [],
    );
    for (const fieldId of controlIds) {
      const raw = params.get(extraParam(source.id, fieldId));
      if (raw === null) continue;
      optionExtras[source.id] = {
        ...(optionExtras[source.id] ?? {}),
        [fieldId]: raw,
      };
    }
  }

  return { scope, selectedOptions, optionExtras };
}

function updateRecommendationParams(
  current: URLSearchParams,
  navigate: ReturnType<typeof useNavigate>,
  pathname: string,
  apply: (next: URLSearchParams) => void,
): void {
  const next = new URLSearchParams(current);
  apply(next);
  navigate({ pathname, search: next.toString() }, { replace: true });
}

function optionParam(sourceId: SourceId): string {
  return `${OPTION_PARAM_PREFIX}${sourceId}`;
}

function extraParam(sourceId: SourceId, fieldId: string): string {
  return `${EXTRA_PARAM_PREFIX}${sourceId}.${fieldId}`;
}

function optionIdForSource(source: ComicSource, selectedOptions: Record<string, string>) {
  return optionForSource(source, selectedOptions)?.id;
}

function optionForSource(
  source: ComicSource,
  selectedOptions: Record<string, string>,
): RecommendationOption | undefined {
  const selected = selectedOptions[source.id];
  return (
    source.recommendationOptions?.find((option) => option.id === selected) ??
    defaultOptionForSource(source)
  );
}

function extraForSource(
  source: ComicSource,
  optionExtras: Record<string, Record<string, unknown>>,
): Record<string, unknown> | undefined {
  return optionExtras[source.id];
}

function defaultOptionForSource(source: ComicSource): RecommendationOption | undefined {
  return (
    source.recommendationOptions?.find((option) => option.default) ??
    source.recommendationOptions?.[0]
  );
}

function ScopeChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-8 shrink-0 rounded-full border px-3 text-xs font-medium',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border/60 text-muted-foreground hover:bg-accent/30 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function RecommendationOptionChip({
  option,
  active,
  onClick,
}: {
  option: RecommendationOption;
  active: boolean;
  onClick: () => void;
}) {
  const hasControls = (option.controls?.length ?? 0) > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-haspopup={hasControls ? 'menu' : undefined}
      className={cn(
        'flex h-8 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-medium',
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border/60 text-muted-foreground hover:bg-accent/30 hover:text-foreground',
      )}
    >
      {hasControls && <SlidersHorizontal className="h-3 w-3" />}
      {option.label}
    </button>
  );
}

function AggregateRecommendations({
  results,
  loading,
  renderItem,
  onOpenSource,
  onSelectOption,
  optionExtras,
  onChangeControl,
}: {
  results: RecommendationResult[];
  loading: boolean;
  renderItem: (comic: ComicCardData) => React.ReactNode;
  onOpenSource: (sourceId: SourceId) => void;
  onSelectOption: (sourceId: SourceId, optionId: string) => void;
  optionExtras: Record<string, Record<string, unknown>>;
  onChangeControl: (sourceId: SourceId, fieldId: string, value: string) => void;
}) {
  const previewCount = 6;
  if (loading && results.length === 0) {
    return <RecommendationSkeleton ariaLabel="Discovery feeds loading" />;
  }
  return (
    <div className="flex flex-col gap-5">
      {results.map((result) => (
        <section key={aggregateSourceSlot(result.source)} className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2 border-b border-border/40 pb-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold">{result.source.name}</h2>
            </div>
            <button
              type="button"
              onClick={() => onOpenSource(result.source.id)}
              className="text-xs text-muted-foreground underline"
            >
              See more
            </button>
          </div>
          {(result.source.recommendationOptions?.length ?? 0) > 0 && (
            <div
              className="flex max-w-full items-center gap-2 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:thin]"
              role="group"
              aria-label={`${result.source.name} recommendation options`}
            >
              {result.source.recommendationOptions?.map((option) => (
                <RecommendationOptionChip
                  key={option.id}
                  option={option}
                  active={result.option?.id === option.id}
                  onClick={() => onSelectOption(result.source.id, option.id)}
                />
              ))}
            </div>
          )}
          <RecommendationControls
            source={result.source}
            option={result.option}
            extras={extraForSource(result.source, optionExtras)}
            onChange={onChangeControl}
          />
          {result.error !== undefined ? (
            <SourceErrorBanner error={result.error} sourceId={result.source.id} />
          ) : result.loading ? (
            <div
              aria-label={`${result.source.name} ${sourceFeedLabel(result.source)} loading`}
              className="flex gap-3 overflow-x-auto pb-2"
            >
              {Array.from({ length: previewCount }).map((_, i) => (
                <Skeleton key={i} className="aspect-[3/4] w-36 shrink-0 sm:w-40" />
              ))}
            </div>
          ) : result.items.length === 0 ? (
            <EmptyRecommendationState source={result.source} />
          ) : (
            <div
              aria-label={`${result.source.name} ${sourceFeedLabel(result.source)}`}
              className="flex gap-3 overflow-x-auto pb-2"
            >
              {result.items.slice(0, previewCount).map((comic) => (
                <div key={`${comic.source}-${comic.id}`} className="w-36 shrink-0 sm:w-40">
                  {renderItem(comic)}
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function RecommendationControls({
  source,
  option,
  extras,
  onChange,
}: {
  source: ComicSource;
  option: RecommendationOption | undefined;
  extras: Record<string, unknown> | undefined;
  onChange: (sourceId: SourceId, fieldId: string, value: string) => void;
}) {
  const controls = option?.controls ?? [];
  if (controls.length === 0) return null;
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border/50 bg-card/20 p-2">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <SlidersHorizontal className="h-3 w-3" />
        Customize {option?.label}
      </div>
      {controls.map((field) => (
        <RecommendationControl
          key={field.id}
          field={field}
          value={controlValue(field, extras)}
          onChange={(value) => onChange(source.id, field.id, value)}
        />
      ))}
    </div>
  );
}

function RecommendationControl({
  field,
  value,
  onChange,
}: {
  field: RecommendationControlDef;
  value: string;
  onChange: (value: string) => void;
}) {
  if (field.type === 'searchable-select') {
    return <SearchableRecommendationSelect field={field} value={value} onChange={onChange} />;
  }
  if (field.type === 'select') {
    return (
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="shrink-0 font-medium uppercase tracking-wide">{field.label}</span>
        <select
          value={value}
          onChange={(event) => onChange(event.currentTarget.value)}
          className="h-8 rounded-md border border-input bg-background px-2 text-xs text-foreground"
        >
          {field.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (field.type === 'chips') {
    return (
      <div className="flex max-w-full items-center gap-2 overflow-x-auto overscroll-x-contain [scrollbar-width:thin]">
        <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {field.label}
        </span>
        {field.options.map((option) => (
          <ScopeChip
            key={option.value}
            active={value === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </ScopeChip>
        ))}
      </div>
    );
  }
  return null;
}

function SearchableRecommendationSelect({
  field,
  value,
  onChange,
}: {
  field: Extract<RecommendationControlDef, { type: 'select' | 'searchable-select' }>;
  value: string;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const selected = field.options.find((option) => option.value === value) ?? field.options[0];
  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = normalizedQuery
    ? field.options.filter(
        (option) =>
          option.label.toLowerCase().includes(normalizedQuery) ||
          option.value.toLowerCase().includes(normalizedQuery),
      )
    : field.options;

  return (
    <div className="relative flex flex-col gap-1 text-xs">
      <span className="font-medium uppercase tracking-wide text-muted-foreground">
        {field.label}
      </span>
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 text-left text-sm"
        aria-expanded={open}
      >
        <span className="truncate">{selected?.label ?? 'Select'}</span>
        <span className="ml-2 text-[10px] uppercase text-muted-foreground">Change</span>
      </button>
      {open && (
        <div className="absolute inset-x-0 top-[calc(100%+0.25rem)] z-30 rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow-xl">
          <input
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder={`Search ${field.label.toLowerCase()}`}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none ring-offset-background focus:ring-2 focus:ring-ring"
            autoFocus
          />
          <div className="mt-2 max-h-64 overflow-y-auto overscroll-contain">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground">No matching themes.</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setQuery('');
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm',
                    option.value === value
                      ? 'bg-secondary text-secondary-foreground'
                      : 'hover:bg-accent',
                  )}
                >
                  <span>{option.label}</span>
                  {option.value && (
                    <span className="ml-3 text-[11px] text-muted-foreground">{option.value}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function controlValue(
  field: RecommendationControlDef,
  extras: Record<string, unknown> | undefined,
): string {
  const raw = extras?.[field.id];
  if (typeof raw === 'string') return raw;
  if ('defaultValue' in field && typeof field.defaultValue === 'string') return field.defaultValue;
  if (field.type === 'chips' || field.type === 'select') return field.options[0]?.value ?? '';
  return '';
}

function RecommendationSkeleton({ ariaLabel }: { ariaLabel: string }) {
  return (
    <div
      aria-label={ariaLabel}
      className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
    >
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} className="aspect-[3/4] w-full" />
      ))}
    </div>
  );
}

function EmptyRecommendationState({ source }: { source: ComicSource }) {
  return (
    <div className="rounded-md border border-dashed border-border/60 p-6 text-center text-sm text-muted-foreground">
      {source.name} did not return any {sourceFeedLabel(source)}.
    </div>
  );
}

function sourceFeedLabel(source: ComicSource): string {
  return source.capabilities.recommendations && source.getRecommendations
    ? 'recommendations'
    : 'popular comics';
}

function UnsupportedState() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>No sources available</CardTitle>
        <CardDescription>Registered source feeds will appear here automatically.</CardDescription>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        Check the source registry diagnostics for load errors.
      </CardContent>
    </Card>
  );
}
